import { useFeaturedContent } from '@gik/api/blog/featuredContent';
import { usePostsFeatured } from '@gik/api/posts/postsFeatured';
import { ArticlesGrid } from '@gik/blog/components/ArticlesGrid/ArticlesGrid';
import { BlogHomepage } from '@gik/blog/components/BlogHomepage/BlogHomepage';
import type Article from '@gik/blog/models/Article';
import { getFeaturedContentTitle } from '@gik/blog/utils/getFeaturedContentTitle';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import type WordpressFeaturedContent from '@gik/core/models/wordpress/WordpressFeaturedContent';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import { useBemCN } from '@gik/core/utils/bemBlock';
import React from 'react';

const blockName = 'blog-homepage-featured-articles-grid';

export interface IBlogHomepageFeaturedArticlesGridProps {
  slug: string;
  perPage: number;
}

export function BlogHomepageFeaturedArticlesGrid({ slug, perPage }: IBlogHomepageFeaturedArticlesGridProps) {
  const bem = useBemCN(blockName);

  const config = {
    componentHeight: 100,
    apiParams: { slug, perPage, fields: BlogHomepage.requiredWPFields.featuredArticlesGrid },
  };

  const featuredArticlesGrid1Skeleton = (
    <ArticlesGrid {...bem()} articles={Array.from(new Array(config.apiParams.perPage))} skeleton />
  );

  return (
    <DynamicComponent<WordpressFeaturedContent[], string>
      height={config.componentHeight}
      fetch={function useFetch() {
        return useFeaturedContent({
          _fields: BlogHomepage.requiredWPFields.featuredContent,
        });
      }}
      transform={featuredContent => getFeaturedContentTitle(featuredContent, config.apiParams.slug)}
      loadingComponent={() => featuredArticlesGrid1Skeleton}
    >
      {({ transformed: title }) => (
        <DynamicComponent<WordpressPost[], Article[]>
          height={config.componentHeight}
          fetch={function useFetch() {
            return usePostsFeatured(config.apiParams);
          }}
          transform={posts =>
            posts.map(post => WordpressConverter.convertPost(post)).filter(WordpressConverter.filterOutPreviewContent)
          }
          loadingComponent={() => featuredArticlesGrid1Skeleton}
        >
          {({ transformed: articles }) => <ArticlesGrid {...bem()} articles={articles} title={title} />}
        </DynamicComponent>
      )}
    </DynamicComponent>
  );
}
